import React, { useState, useEffect } from 'react';
import ImportButton from '../components/ui/ImportButton';
import { CallBtn, WABtn } from '../components/ui/PhoneButtons';
import { supabase } from '../lib/supabase';
import useLiveRefresh from '../hooks/useLiveRefresh';
import toast from 'react-hot-toast';
import { Plus, X, Edit2, Trash2, Search, BookOpen, Megaphone, ClipboardList, Database, Filter } from 'lucide-react';
import NACTETScholarship from './NACTETNew';
import SearchableSelect from '../components/ui/SearchableSelect';
import DateInput from '../components/ui/DateInput';
import { fmtDMY } from '../lib/dateFormat';

// ─── Constants ────────────────────────────────────────────────────
const ENQUIRY_TYPES  = ['Phone','Office','WhatsApp','Email','Webinar Regn','Walk-in','Reference','Social Media','Other'];
const DEMO_TYPES     = ['Offline','Online','Webinar'];
const DEMO_BY_LIST   = ['Others','Faculty','Coordinator','Manager'];
const FINAL_STATUS   = ['Interested','Not Interested','50-50','Enrolled','Follow Up','No Response','Call Not Attended'];
const ADM_STATUS     = ['Enrolled','Not Enrolled','Pending','Cancelled'];
const CENTERS        = ['EKM','TVM'];
const STREAMS        = ['BE-Mech','BE-Civil','DME','Diploma Civil','BE-EEE','BE-ECE','BE-CSE','MCA','MBA','Other'];
const ATTENDED_OPTS  = ['YES','NO','Not Scheduled'];
const REVIEW_OPTS    = ['Enrolled','Interested','Not Interested','50-50','Positive','Negative','Neutral'];
const DOCS_OPTS      = ['YES','NO','Partial'];
const HOSTEL_OPTS    = ['YES','NO','Maybe'];
const SCHOLARSHIP_STATUS = ['Applied','Approved','Rejected','Pending','Disbursed'];
const SCHOLARSHIP_TYPES  = ['Merit','Need Based','Sports','Government','NACTET','Other'];
const MARKETING_CHANNELS = ['Facebook','Instagram','LinkedIn','WhatsApp','Google Ads','College Visit','Webinar','Print','Other'];

// ─── Main NACTET Component ────────────────────────────────────────
export default function NACTET() {
  const [tab, setTab] = useState('scholarship');
  const tabs = [
    { key: 'scholarship', label: 'Scholarship', icon: BookOpen },
    { key: 'demo',        label: 'Demo Feedback',         icon: ClipboardList },
  ];
  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">NACTET Scholarship DB</h1>
          <p className="page-subtitle">Scholarship tracking, marketing, demo feedback & database management</p>
        </div>
      </div>
      <div className="tabs" style={{ marginBottom: 20, flexWrap: 'wrap' }}>
        {tabs.map(({ key, label, icon: Icon }) => (
          <div key={key} className={`tab ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon size={14} />{label}
          </div>
        ))}
      </div>
      {tab === 'scholarship' && <NACTETScholarship />}

      {tab === 'demo'        && <DemoFeedback />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 1. DEMO FEEDBACK (main section — from Excel)
// ─────────────────────────────────────────────────────────────────
function DemoFeedback() {
  const [records, setRecords]   = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [saving, setSaving]     = useState(false);
  const [search, setSearch]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCenter, setFilterCenter] = useState('');
  const [filterStream, setFilterStream] = useState('');
  const [filterCoord,  setFilterCoord]  = useState('');

  // All 26 fields from Excel exactly
  const emptyForm = {
    enquiry_date: '', student_name: '', college_name: '', stream: '',
    phone_no: '', email_id: '', enquiry_type: '', demo_type: '',
    demo_by: '', coordinator: '', demo_date: '', session_time: '',
    attended: '', review: '', final_status: '', followup_date: '',
    admission_status: '', center: '', batch: '', package_name: '',
    reg_fee_paid: '', paid_date: '', payment_ref_no: '',
    docs_received: '', group_name: '', hostel: '',
  };
  const [form, setForm] = useState(emptyForm);
  const sf = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => { load(); }, []);
  useLiveRefresh(['nactet_demo_feedback'], () => load());
  async function load() {
    const { data } = await supabase.from('nactet_demo_feedback').select('*').order('enquiry_date', { ascending: false });
    setRecords(data || []);
  }

  async function save() {
    if (!form.student_name?.trim()) { toast.error('Student name required'); return; }
    setSaving(true);
    const d = v => v && String(v).trim() ? v : null;
    const payload = {
      ...form,
      enquiry_date: d(form.enquiry_date), demo_date: d(form.demo_date),
      followup_date: d(form.followup_date), paid_date: d(form.paid_date),
      reg_fee_paid: form.reg_fee_paid ? Number(form.reg_fee_paid) : null,
    };
    const { error } = editing
      ? await supabase.from('nactet_demo_feedback').update(payload).eq('id', editing)
      : await supabase.from('nactet_demo_feedback').insert(payload);
    if (error) toast.error(error.message);
    else { toast.success(editing ? 'Updated ✅' : 'Saved ✅'); setShowModal(false); load(); }
    setSaving(false);
  }

  function openNew()  { setEditing(null); setForm(emptyForm); setShowModal(true); }
  function openEdit(r){ setEditing(r.id); setForm({ ...emptyForm, ...r, reg_fee_paid: r.reg_fee_paid ? String(r.reg_fee_paid) : '' }); setShowModal(true); }

  // Unique coordinators from data
  const coordinators = [...new Set(records.map(r => r.coordinator).filter(Boolean))].sort();

  const filtered = records.filter(r => {
    const q = search.toLowerCase();
    return (!q || r.student_name?.toLowerCase().includes(q) || r.college_name?.toLowerCase().includes(q) || String(r.phone_no||'').includes(q))
      && (!filterStatus || r.final_status === filterStatus)
      && (!filterCenter || r.center === filterCenter)
      && (!filterStream || r.stream === filterStream)
      && (!filterCoord  || r.coordinator === filterCoord);
  });

  // Summary cards
  const total      = records.length;
  const enrolled   = records.filter(r => r.admission_status === 'Enrolled').length;
  const interested = records.filter(r => r.final_status === 'Interested').length;
  const followup   = records.filter(r => r.final_status === 'Follow Up').length;

  const statusBadge = s => {
    const map = { Enrolled:'badge-green', Interested:'badge-blue', 'Not Interested':'badge-red', 'Follow Up':'badge-amber', '50-50':'badge-cyan' };
    return map[s] || 'badge-gray';
  };

  return (
    <div>
      {/* Summary */}
      <div className="stats-grid" style={{ marginBottom: 16 }}>
        {[
          { label: 'Total Enquiries', value: total,      color: 'var(--accent)' },
          { label: 'Enrolled',        value: enrolled,   color: 'var(--green)' },
          { label: 'Interested',      value: interested, color: 'var(--cyan)' },
          { label: 'Follow Up',       value: followup,   color: 'var(--amber)' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: s.color, fontSize: 22 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
          <div className="search-bar" style={{ minWidth:220 }}>
            <Search /><input placeholder="Search name, college, phone..." value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          <select style={{ width:130 }} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
            <option value="">All Status</option>
            {FINAL_STATUS.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          <select style={{ width:100 }} value={filterCenter} onChange={e=>setFilterCenter(e.target.value)}>
            <option value="">All Centre</option>
            {CENTERS.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <select style={{ width:130 }} value={filterStream} onChange={e=>setFilterStream(e.target.value)}>
            <option value="">All Stream</option>
            {STREAMS.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          <select style={{ width:140 }} value={filterCoord} onChange={e=>setFilterCoord(e.target.value)}>
            <option value="">All Coordinator</option>
            {coordinators.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          {(search||filterStatus||filterCenter||filterStream||filterCoord) &&
            <button className="btn btn-ghost btn-sm" onClick={()=>{setSearch('');setFilterStatus('');setFilterCenter('');setFilterStream('');setFilterCoord('');}}>✕ Clear</button>}
          <span style={{ fontSize:12, color:'var(--text3)' }}>{filtered.length} records</span>
        </div>
        <div style={{display:"flex",gap:8}}><ImportButton configKey="nactet_demo_feedback" onImported={()=>load()}/><button className="btn btn-primary" onClick={openNew}><Plus size={14}/> Add Enquiry</button></div>
      </div>

      {/* Table */}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Date</th><th>Student Name</th><th>College</th><th>Stream</th>
              <th>Phone</th><th>Enquiry Type</th><th>Demo Type</th><th>Demo By</th>
              <th>Coordinator</th><th>Demo Date</th><th>Session Time</th>
              <th>Attended?</th><th>Review</th><th>Final Status</th>
              <th>Followup Date</th><th>Admission Status</th><th>Centre</th>
              <th>Batch</th><th>Package</th><th>Reg Fee (₹)</th>
              <th>Paid Date</th><th>Ref No</th><th>Docs</th><th>Group</th><th>Hostel</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={27}><div className="empty-state"><ClipboardList/><h3>No demo feedback records</h3></div></td></tr>
            ) : filtered.map((r, i) => (
              <tr key={r.id}>
                <td style={{ color:'var(--text3)', fontSize:11 }}>{i+1}</td>
                <td style={{ fontSize:12, whiteSpace:'nowrap' }}>{fmtDMY(r.enquiry_date)||'—'}</td>
                <td style={{ fontWeight:600, whiteSpace:'nowrap' }}>{r.student_name}</td>
                <td style={{ fontSize:11, maxWidth:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.college_name}>{r.college_name||'—'}</td>
                <td style={{ fontSize:11 }}><span className="badge badge-blue" style={{ fontSize:9 }}>{r.stream||'—'}</span></td>
                <td style={{fontSize:12}}><div style={{display:'flex',alignItems:'center',gap:4}}><span style={{fontFamily:'monospace'}}>{r.phone_no||'—'}</span>{r.phone_no&&<CallBtn phone={r.phone_no}/>}{r.whatsapp_no&&<WABtn phone={r.whatsapp_no}/>}</div></td>
                <td style={{ fontSize:11 }}>{r.enquiry_type||'—'}</td>
                <td style={{ fontSize:11 }}>{r.demo_type||'—'}</td>
                <td style={{ fontSize:11 }}>{r.demo_by||'—'}</td>
                <td style={{ fontSize:12, fontWeight:500 }}>{r.coordinator||'—'}</td>
                <td style={{ fontSize:11, whiteSpace:'nowrap' }}>{fmtDMY(r.demo_date)||'—'}</td>
                <td style={{ fontSize:11 }}>{r.session_time||'—'}</td>
                <td>
                  {r.attended ? <span className={`badge ${r.attended==='YES'?'badge-green':r.attended==='NO'?'badge-red':'badge-gray'}`} style={{ fontSize:9 }}>{r.attended}</span> : '—'}
                </td>
                <td style={{ fontSize:11 }}>{r.review||'—'}</td>
                <td>
                  {r.final_status ? <span className={`badge ${statusBadge(r.final_status)}`} style={{ fontSize:9 }}>{r.final_status}</span> : '—'}
                </td>
                <td style={{ fontSize:11, whiteSpace:'nowrap' }}>{fmtDMY(r.followup_date)||'—'}</td>
                <td>
                  {r.admission_status ? <span className={`badge ${r.admission_status==='Enrolled'?'badge-green':'badge-amber'}`} style={{ fontSize:9 }}>{r.admission_status}</span> : '—'}
                </td>
                <td><span className="badge badge-blue" style={{ fontSize:9 }}>{r.center||'—'}</span></td>
                <td style={{ fontSize:11 }}>{r.batch||'—'}</td>
                <td style={{ fontSize:11 }}>{r.package_name||'—'}</td>
                <td style={{ fontSize:12, fontWeight:600, color:'var(--green)' }}>{r.reg_fee_paid ? `₹${Number(r.reg_fee_paid).toLocaleString('en-IN')}` : '—'}</td>
                <td style={{ fontSize:11 }}>{fmtDMY(r.paid_date)||'—'}</td>
                <td style={{ fontSize:11 }}>{r.payment_ref_no||'—'}</td>
                <td>{r.docs_received ? <span className={`badge ${r.docs_received==='YES'?'badge-green':r.docs_received==='NO'?'badge-red':'badge-amber'}`} style={{ fontSize:9 }}>{r.docs_received}</span> : '—'}</td>
                <td style={{ fontSize:11 }}>{r.group_name||'—'}</td>
                <td>{r.hostel ? <span className={`badge ${r.hostel==='YES'?'badge-blue':'badge-gray'}`} style={{ fontSize:9 }}>{r.hostel}</span> : '—'}</td>
                <td>
                  <div style={{ display:'flex', gap:4 }}>
                    <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(r)}><Edit2 size={13}/></button>
                    <button className="btn btn-ghost btn-sm" style={{ color:'var(--red)' }} onClick={async()=>{ if(!window.confirm('Delete?'))return; await supabase.from('nactet_demo_feedback').delete().eq('id',r.id); load(); }}><Trash2 size={13}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div className="modal modal-lg" style={{ maxWidth:820, maxHeight:'92vh', overflowY:'auto' }}>
            <div className="modal-header">
              <span className="modal-title">{editing?'Edit':'Add'} Demo Feedback</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowModal(false)}><X size={16}/></button>
            </div>
            <div className="modal-body">

              {/* Section A: Enquiry Details */}
              <div className="form-section-heading">A — Enquiry Details</div>
              <div className="form-grid">
                <div className="form-group"><label>Date</label><DateInput value={form.enquiry_date} onChange={e=>sf('enquiry_date',e.target.value)}/></div>
                <div className="form-group"><label>Student Name *</label><input value={form.student_name} onChange={e=>sf('student_name',e.target.value)}/></div>
                <div className="form-group full"><label>College Name</label><input value={form.college_name} onChange={e=>sf('college_name',e.target.value)} placeholder="Full college name"/></div>
                <div className="form-group"><label>Stream</label>
                  <select value={form.stream} onChange={e=>sf('stream',e.target.value)}>
                    <option value="">Select Stream</option>
                    {STREAMS.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Phone No</label><input value={form.phone_no} onChange={e=>sf('phone_no',e.target.value)} placeholder="10-digit number"/></div>
                <div className="form-group"><label>Email ID</label><input type="email" value={form.email_id} onChange={e=>sf('email_id',e.target.value)}/></div>
                <div className="form-group"><label>Enquiry Type</label>
                  <select value={form.enquiry_type} onChange={e=>sf('enquiry_type',e.target.value)}>
                    <option value="">Select</option>
                    {ENQUIRY_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Coordinator</label><input value={form.coordinator} onChange={e=>sf('coordinator',e.target.value)} placeholder="Coordinator name"/></div>
              </div>

              {/* Section B: Demo Details */}
              <div className="form-section-heading" style={{ marginTop:14 }}>B — Demo Details</div>
              <div className="form-grid">
                <div className="form-group"><label>Demo Type</label>
                  <select value={form.demo_type} onChange={e=>sf('demo_type',e.target.value)}>
                    <option value="">Select</option>
                    {DEMO_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Demo By</label>
                  <select value={form.demo_by} onChange={e=>sf('demo_by',e.target.value)}>
                    <option value="">Select</option>
                    {DEMO_BY_LIST.map(d=><option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Demo Date</label><DateInput value={form.demo_date} onChange={e=>sf('demo_date',e.target.value)}/></div>
                <div className="form-group"><label>Session Time</label><input value={form.session_time} onChange={e=>sf('session_time',e.target.value)} placeholder="e.g. 10:00 AM"/></div>
                <div className="form-group"><label>Attended?</label>
                  <select value={form.attended} onChange={e=>sf('attended',e.target.value)}>
                    <option value="">Select</option>
                    {ATTENDED_OPTS.map(a=><option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Review</label>
                  <select value={form.review} onChange={e=>sf('review',e.target.value)}>
                    <option value="">Select</option>
                    {REVIEW_OPTS.map(r=><option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>

              {/* Section C: Status & Follow Up */}
              <div className="form-section-heading" style={{ marginTop:14 }}>C — Status & Follow Up</div>
              <div className="form-grid">
                <div className="form-group"><label>Final Status</label>
                  <select value={form.final_status} onChange={e=>sf('final_status',e.target.value)}>
                    <option value="">Select</option>
                    {FINAL_STATUS.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Follow Up Date</label><DateInput value={form.followup_date} onChange={e=>sf('followup_date',e.target.value)}/></div>
                <div className="form-group"><label>Admission Status</label>
                  <select value={form.admission_status} onChange={e=>sf('admission_status',e.target.value)}>
                    <option value="">Select</option>
                    {ADM_STATUS.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              {/* Section D: Enrollment Details */}
              <div className="form-section-heading" style={{ marginTop:14 }}>D — Enrollment Details</div>
              <div className="form-grid">
                <div className="form-group"><label>Centre</label>
                  <select value={form.center} onChange={e=>sf('center',e.target.value)}>
                    <option value="">Select</option>
                    {CENTERS.map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Batch</label><input value={form.batch} onChange={e=>sf('batch',e.target.value)} placeholder="e.g. EM46"/></div>
                <div className="form-group"><label>Package</label><input value={form.package_name} onChange={e=>sf('package_name',e.target.value)} placeholder="e.g. PGDCEM"/></div>
                <div className="form-group"><label>Reg Fee Paid (₹)</label><input type="number" value={form.reg_fee_paid} onChange={e=>sf('reg_fee_paid',e.target.value)}/></div>
                <div className="form-group"><label>Paid Date</label><DateInput value={form.paid_date} onChange={e=>sf('paid_date',e.target.value)}/></div>
                <div className="form-group"><label>Payment Ref No</label><input value={form.payment_ref_no} onChange={e=>sf('payment_ref_no',e.target.value)}/></div>
                <div className="form-group"><label>Docs Received</label>
                  <select value={form.docs_received} onChange={e=>sf('docs_received',e.target.value)}>
                    <option value="">Select</option>
                    {DOCS_OPTS.map(d=><option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Group</label><input value={form.group_name} onChange={e=>sf('group_name',e.target.value)} placeholder="WhatsApp group name"/></div>
                <div className="form-group"><label>Hostel</label>
                  <select value={form.hostel} onChange={e=>sf('hostel',e.target.value)}>
                    <option value="">Select</option>
                    {HOSTEL_OPTS.map(h=><option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':editing?'Update':'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 2. SCHOLARSHIP DB — uses dedicated ScholarshipDB component
// ─────────────────────────────────────────────────────────────────
function ScholarshipDB() {
  return <ScholarshipDBPage />;
}

// ─────────────────────────────────────────────────────────────────
// 3. SCHOLARSHIP MARKETING — uses dedicated component
// ─────────────────────────────────────────────────────────────────
function ScholarshipMarketing() {
  return <ScholarshipMarketingPage />;
}

// ─────────────────────────────────────────────────────────────────
// 4. DB MANAGER
// ─────────────────────────────────────────────────────────────────
function DBManager() {
  const [records, setRecords]     = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [saving, setSaving]       = useState(false);
  const [search, setSearch]       = useState('');
  const [filterStream, setFilterStream] = useState('');

  const emptyForm = {
    student_name:'', college_name:'', stream:'', phone_no:'', email_id:'',
    year_of_passing:'', current_status:'', source:'', remarks:'', center:'',
  };
  const [form, setForm] = useState(emptyForm);
  const sf = (k,v) => setForm(f=>({...f,[k]:v}));

  const STATUS_OPTS = ['Active Lead','Cold Lead','Converted','Not Interested','No Response'];
  const SOURCE_OPTS = ['Demo Feedback','Scholarship DB','College Visit','Referral','Social Media','Webinar','Other'];

  useEffect(()=>{ load(); },[]);
  async function load(){
    const {data}=await supabase.from('nactet_db_manager').select('*').order('created_at',{ascending:false});
    setRecords(data||[]);
  }
  async function save(){
    if(!form.student_name?.trim()){toast.error('Student name required');return;}
    setSaving(true);
    const {error}=editing?await supabase.from('nactet_db_manager').update(form).eq('id',editing):await supabase.from('nactet_db_manager').insert(form);
    if(error)toast.error(error.message);
    else{toast.success('Saved ✅');setShowModal(false);load();}
    setSaving(false);
  }

  const filtered=records.filter(r=>
    (!search||r.student_name?.toLowerCase().includes(search.toLowerCase())||r.college_name?.toLowerCase().includes(search.toLowerCase())||String(r.phone_no||'').includes(search))
    &&(!filterStream||r.stream===filterStream)
  );

  return (
    <div>
      <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <div className="search-bar" style={{minWidth:220}}><Search/><input placeholder="Search name, college, phone..." value={search} onChange={e=>setSearch(e.target.value)}/></div>
          <select style={{width:130}} value={filterStream} onChange={e=>setFilterStream(e.target.value)}>
            <option value="">All Stream</option>
            {STREAMS.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          {(search||filterStream)&&<button className="btn btn-ghost btn-sm" onClick={()=>{setSearch('');setFilterStream('');}}>✕ Clear</button>}
          <span style={{fontSize:12,color:'var(--text3)'}}>{filtered.length} records</span>
        </div>
        <button className="btn btn-primary" onClick={()=>{setEditing(null);setForm(emptyForm);setShowModal(true);}}><Plus size={14}/> Add to DB</button>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>#</th><th>Student Name</th><th>College</th><th>Stream</th><th>Phone</th><th>Email</th><th>Year of Passing</th><th>Status</th><th>Source</th><th>Centre</th><th>Remarks</th><th>Actions</th></tr></thead>
          <tbody>
            {filtered.length===0?<tr><td colSpan={12}><div className="empty-state"><Database/><h3>No records in DB</h3></div></td></tr>
            :filtered.map((r,i)=>(
              <tr key={r.id}>
                <td style={{fontSize:11,color:'var(--text3)'}}>{i+1}</td>
                <td style={{fontWeight:600}}>{r.student_name}</td>
                <td style={{fontSize:11,maxWidth:130,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={r.college_name}>{r.college_name||'—'}</td>
                <td><span className="badge badge-blue" style={{fontSize:9}}>{r.stream||'—'}</span></td>
                <td style={{fontSize:12}}><div style={{display:'flex',alignItems:'center',gap:4}}><span style={{fontFamily:'monospace'}}>{r.phone_no||'—'}</span>{r.phone_no&&<CallBtn phone={r.phone_no}/>}{r.whatsapp_no&&<WABtn phone={r.whatsapp_no}/>}</div></td>
                <td style={{fontSize:11}}>{r.email_id||'—'}</td>
                <td style={{textAlign:'center',fontSize:12}}>{r.year_of_passing||'—'}</td>
                <td><span className={`badge ${r.current_status==='Converted'?'badge-green':r.current_status==='Not Interested'?'badge-red':r.current_status==='Active Lead'?'badge-blue':'badge-gray'}`} style={{fontSize:9}}>{r.current_status||'—'}</span></td>
                <td style={{fontSize:11}}>{r.source||'—'}</td>
                <td><span className="badge badge-cyan" style={{fontSize:9}}>{r.center||'—'}</span></td>
                <td style={{fontSize:11}}>{r.remarks||'—'}</td>
                <td>
                  <div style={{display:'flex',gap:4}}>
                    <button className="btn btn-ghost btn-sm" onClick={()=>{setEditing(r.id);setForm({...emptyForm,...r});setShowModal(true);}}><Edit2 size={13}/></button>
                    <button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={async()=>{if(!window.confirm('Delete?'))return;await supabase.from('nactet_db_manager').delete().eq('id',r.id);load();}}><Trash2 size={13}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showModal&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div className="modal modal-lg" style={{maxWidth:640}}>
            <div className="modal-header"><span className="modal-title">{editing?'Edit':'Add'} DB Record</span><button className="btn btn-ghost btn-sm" onClick={()=>setShowModal(false)}><X size={16}/></button></div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label>Student Name *</label><input value={form.student_name} onChange={e=>sf('student_name',e.target.value)}/></div>
                <div className="form-group full"><label>College Name</label><input value={form.college_name} onChange={e=>sf('college_name',e.target.value)}/></div>
                <div className="form-group"><label>Stream</label><select value={form.stream} onChange={e=>sf('stream',e.target.value)}><option value="">Select</option>{STREAMS.map(s=><option key={s} value={s}>{s}</option>)}</select></div>
                <div className="form-group"><label>Phone No</label><input value={form.phone_no} onChange={e=>sf('phone_no',e.target.value)}/></div>
                <div className="form-group"><label>Email ID</label><input type="email" value={form.email_id} onChange={e=>sf('email_id',e.target.value)}/></div>
                <div className="form-group"><label>Year of Passing</label><input value={form.year_of_passing} onChange={e=>sf('year_of_passing',e.target.value)} placeholder="e.g. 2025"/></div>
                <div className="form-group"><label>Current Status</label><select value={form.current_status} onChange={e=>sf('current_status',e.target.value)}><option value="">Select</option>{STATUS_OPTS.map(s=><option key={s} value={s}>{s}</option>)}</select></div>
                <div className="form-group"><label>Source</label><select value={form.source} onChange={e=>sf('source',e.target.value)}><option value="">Select</option>{SOURCE_OPTS.map(s=><option key={s} value={s}>{s}</option>)}</select></div>
                <div className="form-group"><label>Centre</label><select value={form.center} onChange={e=>sf('center',e.target.value)}><option value="">Select</option>{CENTERS.map(c=><option key={c} value={c}>{c}</option>)}</select></div>
                <div className="form-group full"><label>Remarks</label><textarea rows={2} value={form.remarks} onChange={e=>sf('remarks',e.target.value)}/></div>
              </div>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':editing?'Update':'Save'}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
